/**
 * About this tender: the facts (client, value, deadline) and the bid's prose
 * (summary, evaluation criteria, win themes), kept off the question list so
 * the Tender tab stays a list.
 */
import { ArrowLeftIcon } from "@/design/icons";
import type { FC } from "react";

import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/design/ui/sheet";

import type { TenderDetails } from "../tender/api";
import { insightText } from "../tender/insightText";

export const dateLabel = (raw?: string): string | null => {
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
};

export const TenderAboutSheet: FC<{ open: boolean; tender: TenderDetails; onClose: () => void }> = ({
  open,
  tender,
  onClose,
}) => {
  const deadline = dateLabel(tender.submission_deadline);
  const facts = [
    tender.client_name && { label: "Client", value: tender.client_name },
    tender.value && { label: "Value", value: tender.value },
    deadline && { label: "Due", value: deadline },
  ].filter(Boolean) as { label: string; value: string }[];
  const prose = [
    { label: "Summary", text: insightText(tender.tender_summary) },
    { label: "Evaluation criteria", text: insightText(tender.evaluation_criteria) },
    { label: "Win themes", text: insightText(tender.win_themes) },
  ].filter((p) => p.text.trim());

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full max-w-none gap-0 p-0 sm:max-w-none [&>button]:hidden">
        {open && (
          <div className="flex h-full flex-col bg-surface">
            <div className="flex h-11 shrink-0 items-center gap-1 border-b border-line px-2">
              <button
                type="button"
                aria-label="Back to questions"
                onClick={onClose}
                className="flex size-7 items-center justify-center rounded-sm text-fg-2 hover:bg-subtle hover:text-fg"
              >
                <ArrowLeftIcon className="size-4" />
              </button>
              <span className="text-sm font-medium text-fg-3">About this tender</span>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto pb-4">
              <div className="px-4 pt-4">
                <SheetTitle className="text-lg font-semibold leading-snug text-fg">{tender.bid_title}</SheetTitle>
                <SheetDescription className="sr-only">The tender&apos;s details, summary and criteria</SheetDescription>
              </div>
              {facts.length > 0 && (
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 border-b border-line px-4 pb-3 pt-2 text-sm">
                  {facts.map((f) => (
                    <div key={f.label} className="contents">
                      <dt className="text-fg-3">{f.label}</dt>
                      <dd className="text-fg">{f.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {prose.map((p) => (
                <div key={p.label} className="px-4 pt-3">
                  <div className="text-xs font-semibold text-fg-3">{p.label}</div>
                  <p className="mt-1 whitespace-pre-wrap text-base text-fg-2">{p.text.trim()}</p>
                </div>
              ))}
              {!prose.length && (
                <p className="px-4 pt-3 text-sm text-fg-3">
                  No summary yet. It appears here once mytender has analysed the tender documents.
                </p>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
